import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import * as inventoryApi from '../api/inventoryApi';
import * as locationsApi from '../api/locationsApi';
import type {
  AdjustStockRequest,
  InventoryLocationSummary,
  ReceiveStockRequest,
  StockAlertPageResponse,
  StockAlertsQueryParams,
  StockItemPageResponse,
  StockLedgerEntryPageResponse,
  StockLedgerQueryParams,
  StockListQueryParams,
  StockMutationResponse,
  StockPolicyResponse,
  StockPolicyUpsertRequest,
  StockTransferResponse,
  TransferStockRequest,
} from '../model/types';
import { inventoryKeys } from './queryKeys';

type Opts = { enabled?: boolean };
type Err = NormalizedApiError;

/** Every mutation here can move stock levels and/or cross an alert threshold — refetch all three. */
function invalidateInventoryReads(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: inventoryKeys.stock });
  void qc.invalidateQueries({ queryKey: inventoryKeys.ledger });
  void qc.invalidateQueries({ queryKey: inventoryKeys.alerts });
}

// ── Stock ────────────────────────────────────────────────────────────────────
export function useStock(
  params: StockListQueryParams,
  options?: Opts,
): UseQueryResult<StockItemPageResponse, Err> {
  return useQuery<StockItemPageResponse, Err>({
    queryKey: inventoryKeys.stockList(params),
    queryFn: () => inventoryApi.listStock(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

// ── Movement ledger ──────────────────────────────────────────────────────────
export function useStockLedger(
  params: StockLedgerQueryParams,
  options?: Opts,
): UseQueryResult<StockLedgerEntryPageResponse, Err> {
  return useQuery<StockLedgerEntryPageResponse, Err>({
    queryKey: inventoryKeys.ledgerList(params),
    queryFn: () => inventoryApi.getStockLedger(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

// ── Alerts ───────────────────────────────────────────────────────────────────
export function useStockAlerts(
  params: StockAlertsQueryParams,
  options?: Opts,
): UseQueryResult<StockAlertPageResponse, Err> {
  return useQuery<StockAlertPageResponse, Err>({
    queryKey: inventoryKeys.alertsList(params),
    queryFn: () => inventoryApi.listStockAlerts(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

// ── Movements: receipts / adjustments / transfers ───────────────────────────
export function useReceiveStock() {
  const qc = useQueryClient();
  return useMutation<StockMutationResponse, Err, ReceiveStockRequest>({
    mutationFn: inventoryApi.receiveStock,
    onSuccess: () => invalidateInventoryReads(qc),
  });
}

export function useAdjustStock() {
  const qc = useQueryClient();
  return useMutation<StockMutationResponse, Err, AdjustStockRequest>({
    mutationFn: inventoryApi.adjustStock,
    onSuccess: () => invalidateInventoryReads(qc),
  });
}

export function useTransferStock() {
  const qc = useQueryClient();
  return useMutation<StockTransferResponse, Err, TransferStockRequest>({
    mutationFn: inventoryApi.transferStock,
    onSuccess: () => invalidateInventoryReads(qc),
  });
}

// ── Policies ─────────────────────────────────────────────────────────────────
export function useStockPolicy(
  productId: string | undefined,
  locationId: string | undefined,
  options?: Opts,
): UseQueryResult<StockPolicyResponse, Err> {
  return useQuery<StockPolicyResponse, Err>({
    queryKey: inventoryKeys.policy(productId ?? '', locationId ?? ''),
    queryFn: () => inventoryApi.getStockPolicy(productId as string, locationId as string),
    enabled: (options?.enabled ?? true) && Boolean(productId) && Boolean(locationId),
  });
}

export function useUpsertStockPolicy() {
  const qc = useQueryClient();
  return useMutation<
    StockPolicyResponse,
    Err,
    { productId: string; locationId: string; body: StockPolicyUpsertRequest }
  >({
    mutationFn: ({ productId, locationId, body }) =>
      inventoryApi.upsertStockPolicy(productId, locationId, body),
    onSuccess: (data, { productId, locationId }) => {
      qc.setQueryData(inventoryKeys.policy(productId, locationId), data);
      // A tightened/loosened threshold can immediately change which rows breach it.
      void qc.invalidateQueries({ queryKey: inventoryKeys.alerts });
      void qc.invalidateQueries({ queryKey: inventoryKeys.stock });
    },
  });
}

// ── Locations (id → name enrichment) ────────────────────────────────────────
export function useLocations(options?: Opts): UseQueryResult<InventoryLocationSummary[], Err> {
  return useQuery<InventoryLocationSummary[], Err>({
    queryKey: inventoryKeys.locations,
    queryFn: locationsApi.searchAllLocations,
    enabled: options?.enabled ?? true,
    // Locations rarely change; a longer staleTime avoids a refetch on every tab switch.
    staleTime: 5 * 60_000,
  });
}
